import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faCheckDouble } from "@fortawesome/pro-solid-svg-icons/faCheckDouble";
import { faMoneyBillWave } from "@fortawesome/pro-solid-svg-icons/faMoneyBillWave";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { endOfToday, isAfter } from "date-fns/esm";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import DateRangeType from "@mapmycustomers/shared/enum/DateRangeType";
import { StandardRange } from "@mapmycustomers/shared/types/range/DateRange";
import User from "@mapmycustomers/shared/types/User";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { Labeled } from "@mapmycustomers/ui";

import CriteriaExtraFilters from "@app/component/CriteriaExtraFilters";
import ButtonGroup from "@app/component/input/ButtonGroup";
import { Range, RangeTypeSelect } from "@app/component/input/dateRangeSelect";
import UserSelectFilter from "@app/component/UserSelectFilter";
import Sidebar from "@app/scene/map/components/sidebar/Sidebar";
import {
  updateHeatMapAdditionalFilters,
  updateHeatMapDateRange,
  updateHeatMapUsers,
} from "@app/scene/map/store/actions";
import HeatMapLayerData from "@app/scene/map/store/layers/heatMap/HeatMapLayerData";
import { getHeatMapLayerData } from "@app/scene/map/store/selectors";
import DateRange from "@app/scene/reports/types/DateRange";
import { RootState } from "@app/store/rootReducer";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import parseRange from "@app/util/range/parseRange";
import standardRangeToDateRange from "@app/util/range/standardRangeToDateRange";

import ActivityHeatMapSettings from "./ActivityHeatMapSettings";
import DealHeatMapSettings from "./DealHeatMapSettings";
import styles from "./HeatMapPanel.module.scss";
import messages from "./messages";

const hiddenExtraFilterFields = [
  ActivityFieldName.ACTIVITY_TYPE,
  ActivityFieldName.COMPLETED_AT,
  ActivityFieldName.ASSIGNEE,
  DealFieldName.CLOSING_DATE,
  DealFieldName.USER,
  DealFieldName.FUNNEL,
  DealFieldName.STAGE_TYPE,
];

export const futureDateDisabled = (date: Date) => isAfter(date, endOfToday());

interface Props {
  data: HeatMapLayerData;
  onHide: () => void;
  onUpdateHeatMapAdditionalFilters?: (payload: {
    additionalFilters: PlatformFilterModel;
    type: HeatMapLayerData["type"];
  }) => void;
  onUpdateHeatMapDateRange?: (payload: {
    dateRange: DateRange | undefined;
    dateRangeType: DateRangeType.ALL | DateRangeType.CUSTOM | StandardRange;
  }) => void;
  onUpdateHeatMapType?: (payload: HeatMapLayerData["type"]) => void;
  onUpdateHeatMapUsers: (payload: User["id"][]) => void;
}

const HeatMapPanel: React.FC<Props> = ({
  data,
  onHide,
  onUpdateHeatMapAdditionalFilters,
  onUpdateHeatMapDateRange,
  onUpdateHeatMapType,
  onUpdateHeatMapUsers,
}) => {
  const intl = useIntl();
  const entityType = data.type;
  const [newDateRangeType, setNewDateRangeType] = useState<
    DateRangeType.ALL | DateRangeType.CUSTOM | StandardRange
  >(data.dateRangeType ?? DateRangeType.ALL);
  const [newDateRange, setNewDateRange] = useState<DateRange | undefined>(data.dateRange);

  const handleChangeRangeType = useCallback((value: DateRangeType) => {
    setNewDateRangeType(
      value === DateRangeType.CUSTOM || value === DateRangeType.ALL ? value : `${value}0`
    );
  }, []);

  useEffect(() => {
    const dateRange =
      newDateRangeType === DateRangeType.ALL
        ? undefined
        : newDateRangeType === DateRangeType.CUSTOM
        ? newDateRange
        : standardRangeToDateRange(newDateRangeType);

    onUpdateHeatMapDateRange?.({ dateRange, dateRangeType: newDateRangeType });
  }, [newDateRange, newDateRangeType, onUpdateHeatMapDateRange]);

  const hiddenFields = useMemo(() => {
    const fieldModel = entityType ? getFieldModelByEntityType(entityType) : undefined;

    return fieldModel
      ? hiddenExtraFilterFields
          .map((fieldName) => fieldModel.getByName(fieldName))
          .filter(isDefined)
      : undefined;
  }, [entityType]);

  const handleFilterChange = useCallback(
    (additionalFilters: PlatformFilterModel) => {
      onUpdateHeatMapAdditionalFilters?.({ additionalFilters, type: data.type });
    },
    [data.type, onUpdateHeatMapAdditionalFilters]
  );

  return (
    <Sidebar mask={false} onClose={onHide} title={intl.formatMessage(messages.edit)} visible>
      <div className={styles.content}>
        <Labeled label={intl.formatMessage(messages.type)}>
          <ButtonGroup<HeatMapLayerData["type"]> onChange={onUpdateHeatMapType} value={entityType}>
            <ButtonGroup.Item
              icon={<ButtonGroup.Icon icon={faCheckDouble} />}
              title={intl.formatMessage(messages.activity)}
              value={EntityType.ACTIVITY}
            />
            <ButtonGroup.Item
              icon={<ButtonGroup.Icon icon={faMoneyBillWave} />}
              title={intl.formatMessage(messages.deal)}
              value={EntityType.DEAL}
            />
          </ButtonGroup>
        </Labeled>
        <RangeTypeSelect
          allowAll
          onChange={handleChangeRangeType}
          value={parseRange(data.dateRangeType)?.dateRangeType ?? DateRangeType.ALL}
        />
        <Range
          dateRange={data.dateRange}
          dateRangeType={data.dateRangeType ?? DateRangeType.ALL}
          isCustomDateDisabled={futureDateDisabled}
          label={intl.formatMessage(
            entityType === EntityType.ACTIVITY ? messages.dateDone : messages.closingDate
          )}
          onChangeDateRange={setNewDateRange}
          onChangeDateRangeType={setNewDateRangeType}
        />
        {entityType === EntityType.ACTIVITY ? (
          <ActivityHeatMapSettings data={data} />
        ) : entityType === EntityType.DEAL ? (
          <DealHeatMapSettings data={data} />
        ) : null}
        <Labeled
          label={intl.formatMessage(messages.users, {
            activityType: entityType === EntityType.ACTIVITY,
          })}
        >
          <UserSelectFilter
            className={styles.userFilter}
            onChange={onUpdateHeatMapUsers}
            value={data.userIds}
          />
        </Labeled>
        <CriteriaExtraFilters
          buttonProps={{ leftIcon: <FontAwesomeIcon icon={faPlus} />, type: "filled" }}
          buttonText={intl.formatMessage(messages.additionalFilters)}
          className={styles.filters}
          context={`map/heatMap/${entityType}`}
          entityType={entityType}
          hiddenFields={hiddenFields}
          onChange={handleFilterChange}
          value={
            data.type === EntityType.DEAL
              ? data.dealAdditionalFilters
              : data.activityAdditionalFilters
          }
        />
      </div>
    </Sidebar>
  );
};

const mapStateToProps = (state: RootState) => ({
  data: getHeatMapLayerData(state),
});

const mapDispatchToProps = {
  onUpdateHeatMapAdditionalFilters: updateHeatMapAdditionalFilters,
  onUpdateHeatMapDateRange: updateHeatMapDateRange,
  onUpdateHeatMapUsers: updateHeatMapUsers,
};

export default connect(mapStateToProps, mapDispatchToProps)(HeatMapPanel);
